// src/components/SendNotification/SendNotification.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faPaperPlane,
    faEnvelope,
    faBell,
    faUsers,
    faFileAlt,
    faCog,
    faCheck,
    faTimes,
    faSpinner
} from '@fortawesome/free-solid-svg-icons';
import { faTelegram } from '@fortawesome/free-brands-svg-icons';
import { useNotificationContext } from '../../contexts/NotificationContext';
import { get } from '../../helpers/apiHelper';
import useAuthStore from '../../store/authStore';

const SendNotification = () => {
    const { user } = useAuthStore();
    const {
        selectedMarca,
        setSelectedMarca,
        isChannelEnabled,
        templates,
        sendNotification,
        loading
    } = useNotificationContext();

    const [marcas, setMarcas] = useState([]);
    const [clientes, setClientes] = useState([]);
    const [selectedClientes, setSelectedClientes] = useState([]);
    const [selectedChannel, setSelectedChannel] = useState('email');
    const [selectedTemplate, setSelectedTemplate] = useState('');
    const [templateVariables, setTemplateVariables] = useState({});
    const [customMessage, setCustomMessage] = useState('');
    const [useCustomMessage, setUseCustomMessage] = useState(false);
    const [scheduledDate, setScheduledDate] = useState('');
    const [scheduledTime, setScheduledTime] = useState('');
    const [sendImmediately, setSendImmediately] = useState(true);
    const [result, setResult] = useState(null);

    const loadMarcas = useCallback(async () => {
        try {
            const response = await get(`marcas/user/${user.userId}`);
            setMarcas(response);

            if (response.length > 0 && !selectedMarca) {
                setSelectedMarca(response[0]._id);
            }
        } catch (error) {
            console.error('Error cargando marcas:', error);
        }
    }, [user?.userId, selectedMarca, setSelectedMarca]);

    const loadClientes = useCallback(async () => {
        try {
            const response = await get(`clientes/marca/${selectedMarca}`);
            setClientes(response);
        } catch (error) {
            console.error('Error cargando clientes:', error);
        }
    }, [selectedMarca]);

    useEffect(() => {
        if (user?.userId) {
            loadMarcas();
        }
    }, [user, loadMarcas]);

    useEffect(() => {
        if (selectedMarca) {
            loadClientes();
        }
    }, [selectedMarca, loadClientes]);

    const handleClienteSelection = (clienteId, checked) => {
        if (checked) {
            setSelectedClientes(prev => [...prev, clienteId]);
        } else {
            setSelectedClientes(prev => prev.filter(id => id !== clienteId));
        }
    };

    const handleSelectAll = (checked) => {
        if (checked) {
            setSelectedClientes(clientes.map(cliente => cliente._id));
        } else {
            setSelectedClientes([]);
        }
    };

    const handleTemplateChange = (templateId) => {
        setSelectedTemplate(templateId);
        const template = templates.find(t => t._id === templateId);
        if (template) {
            // Extraer variables del template
            const variables = extractVariablesFromTemplate(template.content);
            const initialVariables = {};
            variables.forEach(variable => {
                initialVariables[variable] = '';
            });
            setTemplateVariables(initialVariables);
        }
    };

    const extractVariablesFromTemplate = (content) => {
        const variableRegex = /\{\{(\w+)\}\}/g;
        const variables = [];
        let match;
        while ((match = variableRegex.exec(content)) !== null) {
            variables.push(match[1]);
        }
        return [...new Set(variables)]; // Eliminar duplicados
    };

    const getTemplatePreview = () => {
        if (!selectedTemplate) return '';

        const template = templates.find(t => t._id === selectedTemplate);
        if (!template) return '';

        let preview = template.content;
        Object.keys(templateVariables).forEach(variable => {
            const value = templateVariables[variable] || `{{${variable}}}`;
            preview = preview.replace(new RegExp(`\\{\\{${variable}\\}\\}`, 'g'), value);
        });

        return preview;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (selectedClientes.length === 0) {
            setResult({ success: false, message: 'Debes seleccionar al menos un cliente' });
            return;
        }

        if (!isChannelEnabled(selectedChannel)) {
            setResult({ success: false, message: `El canal ${selectedChannel} no está configurado` });
            return;
        }

        try {
            const notificationData = {
                channel: selectedChannel,
                clientes: selectedClientes,
                template: selectedTemplate,
                variables: templateVariables,
                customMessage: useCustomMessage ? customMessage : null,
                scheduledAt: sendImmediately ? null : `${scheduledDate}T${scheduledTime}:00.000Z`
            };

            const response = await sendNotification(notificationData);

            setResult({
                success: true,
                message: `Notificación enviada correctamente a ${selectedClientes.length} cliente(s)`,
                data: response
            });

            // Limpiar formulario
            setSelectedClientes([]);
            setTemplateVariables({});
            setCustomMessage('');

        } catch (error) {
            setResult({ success: false, message: error.message });
        }
    };

    const channels = [
        { id: 'email', name: 'Email', icon: faEnvelope, color: 'text-blue-500' },
        { id: 'telegram', name: 'Telegram', icon: faTelegram, color: 'text-blue-400' },
        { id: 'push', name: 'Push', icon: faBell, color: 'text-orange-500' }
    ];

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="bg-white rounded-lg shadow-md p-6">
                <div className="flex items-center mb-4">
                    <FontAwesomeIcon icon={faPaperPlane} className="text-blue-500 text-2xl mr-3" />
                    <div>
                        <h2 className="text-2xl font-bold text-gray-800">
                            Enviar Notificaciones
                        </h2>
                        <p className="text-gray-600">
                            Envía notificaciones personalizadas a tus clientes
                        </p>
                    </div>
                </div>

                {/* Selector de marca */}
                <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Seleccionar Marca
                    </label>
                    <select
                        value={selectedMarca || ''}
                        onChange={(e) => setSelectedMarca(e.target.value)}
                        className="w-full md:w-64 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                        {marcas.map((marca) => (
                            <option key={marca._id} value={marca._id}>
                                {marca.nombre}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
                {/* Selección de clientes */}
                <div className="bg-white rounded-lg shadow-md p-6">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4">
                        <FontAwesomeIcon icon={faUsers} className="mr-2" />
                        Seleccionar Clientes
                    </h3>

                    <div className="mb-4">
                        <label className="flex items-center">
                            <input
                                type="checkbox"
                                checked={selectedClientes.length === clientes.length && clientes.length > 0}
                                onChange={(e) => handleSelectAll(e.target.checked)}
                                className="mr-2"
                            />
                            <span className="text-sm font-medium text-gray-700">
                                Seleccionar todos ({clientes.length} clientes)
                            </span>
                        </label>
                    </div>

                    <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-md p-4">
                        {clientes.map((cliente) => (
                            <label key={cliente._id} className="flex items-center py-2 hover:bg-gray-50">
                                <input
                                    type="checkbox"
                                    checked={selectedClientes.includes(cliente._id)}
                                    onChange={(e) => handleClienteSelection(cliente._id, e.target.checked)}
                                    className="mr-3"
                                />
                                <div>
                                    <div className="font-medium text-gray-800">
                                        {cliente.nombre} {cliente.apellido}
                                    </div>
                                    <div className="text-sm text-gray-600">
                                        {cliente.email} • {cliente.telefono}
                                    </div>
                                </div>
                            </label>
                        ))}
                    </div>

                    <div className="mt-3 text-sm text-gray-600">
                        {selectedClientes.length} cliente(s) seleccionado(s)
                    </div>
                </div>

                {/* Configuración de notificación */}
                <div className="bg-white rounded-lg shadow-md p-6">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4">
                        <FontAwesomeIcon icon={faCog} className="mr-2" />
                        Configuración de Notificación
                    </h3>

                    {/* Selección de canal */}
                    <div className="mb-6">
                        <label className="block text-sm font-medium text-gray-700 mb-3">
                            Canal de Envío
                        </label>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            {channels.map((channel) => (
                                <label
                                    key={channel.id}
                                    className={`flex items-center p-3 border-2 rounded-lg cursor-pointer transition-colors ${selectedChannel === channel.id
                                        ? 'border-blue-500 bg-blue-50'
                                        : 'border-gray-200 hover:border-gray-300'
                                        } ${!isChannelEnabled(channel.id) ? 'opacity-50 cursor-not-allowed' : ''}`}
                                >
                                    <input
                                        type="radio"
                                        name="channel"
                                        value={channel.id}
                                        checked={selectedChannel === channel.id}
                                        onChange={(e) => setSelectedChannel(e.target.value)}
                                        disabled={!isChannelEnabled(channel.id)}
                                        className="sr-only"
                                    />
                                    <FontAwesomeIcon
                                        icon={channel.icon}
                                        className={`text-xl mr-3 ${channel.color}`}
                                    />
                                    <div>
                                        <div className="font-medium text-gray-800">{channel.name}</div>
                                        <div className="text-xs text-gray-500">
                                            {isChannelEnabled(channel.id) ? 'Disponible' : 'No configurado'}
                                        </div>
                                    </div>
                                </label>
                            ))}
                        </div>
                    </div>

                    {/* Selección de template */}
                    <div className="mb-6">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            <FontAwesomeIcon icon={faFileAlt} className="mr-2" />
                            Template de Mensaje
                        </label>
                        <select
                            value={selectedTemplate}
                            onChange={(e) => handleTemplateChange(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="">Seleccionar template...</option>
                            {templates.map((template) => (
                                <option key={template._id} value={template._id}>
                                    {template.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Variables del template */}
                    {selectedTemplate && Object.keys(templateVariables).length > 0 && (
                        <div className="mb-6">
                            <label className="block text-sm font-medium text-gray-700 mb-3">
                                Variables del Template
                            </label>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {Object.keys(templateVariables).map((variable) => (
                                    <div key={variable}>
                                        <label className="block text-sm text-gray-600 mb-1">
                                            {variable}
                                        </label>
                                        <input
                                            type="text"
                                            value={templateVariables[variable]}
                                            onChange={(e) => setTemplateVariables(prev => ({
                                                ...prev,
                                                [variable]: e.target.value
                                            }))}
                                            placeholder={`Valor para ${variable}`}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Mensaje personalizado */}
                    <div className="mb-6">
                        <label className="flex items-center mb-2">
                            <input
                                type="checkbox"
                                checked={useCustomMessage}
                                onChange={(e) => setUseCustomMessage(e.target.checked)}
                                className="mr-2"
                            />
                            <span className="text-sm font-medium text-gray-700">
                                Usar mensaje personalizado
                            </span>
                        </label>
                        {useCustomMessage && (
                            <textarea
                                value={customMessage}
                                onChange={(e) => setCustomMessage(e.target.value)}
                                placeholder="Escribe tu mensaje personalizado..."
                                rows={4}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        )}
                    </div>

                    {/* Vista previa */}
                    {selectedTemplate && !useCustomMessage && (
                        <div className="mb-6">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Vista Previa
                            </label>
                            <div className="p-4 bg-gray-50 border border-gray-200 rounded-md">
                                <div className="whitespace-pre-wrap text-gray-800">
                                    {getTemplatePreview()}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Programación */}
                    <div className="mb-6">
                        <label className="flex items-center mb-3">
                            <input
                                type="checkbox"
                                checked={sendImmediately}
                                onChange={(e) => setSendImmediately(e.target.checked)}
                                className="mr-2"
                            />
                            <span className="text-sm font-medium text-gray-700">
                                Enviar inmediatamente
                            </span>
                        </label>

                        {!sendImmediately && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm text-gray-600 mb-1">
                                        Fecha
                                    </label>
                                    <input
                                        type="date"
                                        value={scheduledDate}
                                        onChange={(e) => setScheduledDate(e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm text-gray-600 mb-1">
                                        Hora
                                    </label>
                                    <input
                                        type="time"
                                        value={scheduledTime}
                                        onChange={(e) => setScheduledTime(e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Resultado */}
                {result && (
                    <div className={`p-4 rounded-md ${result.success
                        ? 'bg-green-50 border border-green-200 text-green-800'
                        : 'bg-red-50 border border-red-200 text-red-800'
                        }`}>
                        <div className="flex items-center">
                            <FontAwesomeIcon
                                icon={result.success ? faCheck : faTimes}
                                className={`mr-2 ${result.success ? 'text-green-600' : 'text-red-600'}`}
                            />
                            {result.message}
                        </div>
                    </div>
                )}

                {/* Botón de envío */}
                <div className="flex justify-end">
                    <button
                        type="submit"
                        disabled={loading || selectedClientes.length === 0}
                        className="px-6 py-3 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                    >
                        {loading ? (
                            <>
                                <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                                Enviando...
                            </>
                        ) : (
                            <>
                                <FontAwesomeIcon icon={faPaperPlane} className="mr-2" />
                                Enviar Notificación
                            </>
                        )}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default SendNotification; 